/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withActionHandler } from '../utils/withActionHandler';
import { cleanUpKey, createObjectBlob, generateNameOfApi } from '../utils/common';
import { useApi } from '../contexts/ApiHandler';
import { useConfig } from '../contexts/config';
import Markdown from './Markdown';
import SecurityDisplay from './SecurityDisplay';
import AsyncapiBindings from './AsyncapiBindings';
import { retrieveDocHtml } from '../utils/documentation-loader';
import { Tag, Link, Loading, Modal, ToastNotification, Select, SelectItem } from '@carbon/react';
import { isIE } from '../utils/common';
import ExtensionsDisplay from './ExtensionsDisplay';
import { decycle } from 'json-decycle';
import { getTranslatedExtensions } from '../utils/translate';
import { APITypes } from 'utils/APITypes';
import { displayType, isKafka, isKafkaSecure } from 'utils/ProtocolTypes';
import { handleKeyDown } from 'utils/common';
import { Download, Launch } from '@carbon/react/icons';

import OperationTag from './OperationTag';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
// Set a maximum size on OAI documents for the validator. A document larger than this value will not be validated.
const MAX_OAPI_DOCUMENT_SIZE_KB = 100; // kb (approximate!)

const getInfoSectionEntries = (api, wsdlRetrieverURL, apiHandler, infoSectionEntries, url) => {
  let infoSectionEntriesJSON = infoSectionEntries;
  if (infoSectionEntriesJSON && apiHandler.isSOAP(api)) {
    const currentEndpoint = url ? url : apiHandler.getEndpoints(api, {})[0].url;
    infoSectionEntriesJSON = JSON.parse(JSON.stringify(infoSectionEntries));

    infoSectionEntriesJSON.forEach((infoSectionEntry) => {
      if (
        infoSectionEntry.location === 'OVERVIEW' &&
        infoSectionEntry.class.includes('apic_attachments') &&
        infoSectionEntry.html &&
        infoSectionEntry.html.includes('apiwsdl')
      ) {
        let newDownloadURL = wsdlRetrieverURL.replace('REPLACE_THIS_VALUE', btoa(currentEndpoint));
        infoSectionEntry.html = infoSectionEntry.html.replace(/https:\/\/.*(\.wsdl|\.zip)"/i, newDownloadURL + '"');
      }
    });
  }

  return infoSectionEntriesJSON;
};

const Overview = ({ infoSectionEntries, documentationEntries }) => {
  const { t } = useTranslation();
  const [modalState, updateModalState] = useState({ modalContent: null, modalTitle: null, modalOpen: null, modalMarkdown: null });
  const [swaggerState, updateSwaggerState] = useState({
    showSwaggerMessages: true,
    swaggerErrors: null,
    swaggerValidationSkipped: false,
    swaggerValidationOverride: false
  });
  const [infoSectionEntriesState, updateInfoSectionEntriesState] = useState(null);
  const [yamlFileContentState, updateYamlFileContentState] = useState({});
  const [jsonFileContentState, updateJsonFileContentState] = useState({});

  const { options } = useConfig();
  const { api, apiHandler } = useApi();
  const _isMounted = useRef(false);

  const handleEndpointChange = (event) => {
    const url = event.target.value;
    updateInfoSectionEntriesState(getInfoSectionEntries(api, options.wsdlRetrieverURL, apiHandler, infoSectionEntries, url));
  };

  const hideSwaggerMessages = () => {
    updateSwaggerState((prev) => ({ ...prev, showSwaggerMessages: false }));
  };

  const downloadApiForIE = (event, type = 'yaml') => {
    let tabKey = 9;
    if (event && event.which === tabKey) {
      return;
    }

    if (isIE()) {
      event.preventDefault();
      if (type === 'yaml') {
        navigator.msSaveBlob(yamlFileContentState.fileContent, yamlFileContentState.filename);
      } else {
        navigator.msSaveBlob(jsonFileContentState.fileContent, jsonFileContentState.filename);
      }
    }
  };

  const showModal = withActionHandler(async (_, content) => {
    let newModalState = {
      modalTitle: content.title,
      modalContent: null,
      modalOpen: true,
      modalMarkdown: content.markdown
    };

    if (content && content.url) {
      // fire the download service to get the content, can't be cancelled
      retrieveDocHtml(content.url, content.extractPortalContent).then(
        (html) => {
          if (_isMounted.current) {
            updateModalState((modalState) => ({ ...modalState, modalContent: html }));
          }
        },
        (error) => {
          if (_isMounted.current) {
            updateModalState((modalState) => ({ ...modalState, modalContent: error }));
          }
        }
      );
    } else {
      newModalState.modalContent = content.content;
    }
    updateModalState(newModalState);
  });

  const hideDocumentation = withActionHandler(() => {
    updateModalState((modalState) => ({ ...modalState, modalOpen: false }));
  });

  const validateApi = (api, validateSwagger, override) => {
    const newSwaggerState = {
      swaggerErrors: null,
      showSwaggerMessages: true,
      swaggerValidationSkipped: false,
      swaggerValidationOverride: false
    };

    // only run validation if App was configured for it (default is true)
    if (validateSwagger && validateSwagger === false) {
      newSwaggerState.showSwaggerMessages = false;
      updateSwaggerState(newSwaggerState);
      return;
    }

    // validation of async-api is done by NavigationHandler
    if (api && !api.asyncapi) {
      if (Array.isArray(api.paths) && api.paths.length === 0) {
        api.paths = {};
      } else if (Object.keys(api.paths).length >= 1) {
        for (let key in api.paths) {
          if (Array.isArray(api.paths[key]) && api.paths[key].length === 0) {
            api.paths[key] = {};
          }
        }
      }

      let apiString = JSON.stringify(api, decycle());

      // if the API document is huge, don't validate it but allow the user the opportunity to crash their browser if they want
      if (!override && apiString.length * 2 > MAX_OAPI_DOCUMENT_SIZE_KB * 1024) {
        newSwaggerState.swaggerValidationSkipped = true;
        updateSwaggerState(newSwaggerState);
        return;
      }

      let dapi = JSON.parse(apiString);

      let SwaggerParser = require('swagger-parser');
      SwaggerParser.validate(dapi, (err, dapi) => {
        if (_isMounted.current) {
          if (err) {
            newSwaggerState.swaggerErrors = err.message;
            newSwaggerState.swaggerValidationSkipped = false;
          } else {
            newSwaggerState.swaggerErrors = null;
            newSwaggerState.swaggerValidationSkipped = false;
          }

          updateSwaggerState(newSwaggerState);
        }
      });
    }
  };

  useEffect(() => {
    _isMounted.current = true;
    return () => {
      _isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    updateYamlFileContentState(createObjectBlob(api, generateNameOfApi(api), options.removeIBMKeys));
    updateJsonFileContentState(createObjectBlob(api, generateNameOfApi(api), options.removeIBMKeys, 'json'));
    updateInfoSectionEntriesState(getInfoSectionEntries(api, options.wsdlRetrieverURL, apiHandler, infoSectionEntries));
  }, [api, options.removeIBMKeys, options.wsdlRetrieverURL, apiHandler, infoSectionEntries]);

  useEffect(() => {
    validateApi(api, options.validateSwagger, false);
  }, [api, options.validateSwagger]);

  const renderHeader = () => {
    const apiTags = apiHandler.getApiTags(api);
    const tagLabels = apiTags ? Object.keys(apiTags.tags) : null;
    const markupType = apiHandler.getMarkupType();

    return (
      <section className="overviewHeader" aria-label="overview-header">
        <h2>{t('overview')}</h2>
        <div className="operationTags tagsContainer">
          {tagLabels &&
            tagLabels.length > 0 &&
            tagLabels.map(function (tag) {
              return <OperationTag key={tag} tag={tag} apiTags={apiTags} markupType={markupType} />;
            })}
        </div>
      </section>
    );
  };

  const renderSwaggerErrors = (swaggerErrors) => {
    return (
      <section className="" aria-label="swagger-validation-errors" data-testid={'overview-swagger-errors'}>
        <ToastNotification
          kind="error"
          role="alert"
          title={t('swagger_errors')}
          subtitle={t('swagger_errors_description')}
          className="errorFrame"
          iconDescription={t('close_swagger_errors')}
          onCloseButtonClick={hideSwaggerMessages}
        >
          <pre className="validationErrorText">
            <code>{swaggerErrors}</code>
          </pre>
        </ToastNotification>
      </section>
    );
  };

  const renderDescription = (markupType) => {
    return (
      <div className="operationInformation apiInfoDescription divided__item" data-testid={'operation-information'}>
        <div role="heading" aria-level={3}  className="labelText">{t('description')}</div>
        <div className="valueText">
          <Markdown data={getTranslatedExtensions(api.info, 'description')} markupType={markupType} />
        </div>
      </div>
    );
  };

  const renderSummary = (summary) => {
    return summary ? (
      <p className="apiSummary" data-testid={'overview-summary'}>
        {summary}
      </p>
    ) : null;
  };

  const renderType = () => {
    let apiType = apiHandler.getType(api);
    const specLabel = apiHandler.getSpecLabel(api);

    if (apiType === APITypes.asyncapi) {
      const docLink = get(options, 'apiTypeDocs.asyncapi');
      if (docLink) {
        apiType = (
          <Link href={docLink} target="_blank" rel="noopener noreferrer" title={t('async_doc_link_title')}>
            {apiType}
            <Launch className="externalLink" />
          </Link>
        );
      }
    }

    return (
      <div className="operationInformation operationType divided__item" data-testid={'operation-type'}>
        <div role="heading" aria-level={3}  className="labelText">{t('type')}</div>
        <div className="valueText" data-testid="apiType">
          {apiType} {specLabel && <Tag className="apiSpecLabel">{specLabel}</Tag>}
        </div>
      </div>
    );
  };

  const renderServerBindings = (endpoint) => {
    const serverBindings = endpoint.bindings;
    return serverBindings && <AsyncapiBindings bindings={serverBindings} />;
  };

  const renderEndpoints = (endpoints) => {
    const isSoap = apiHandler.isSOAP(api);
    const isAsync = apiHandler.isAsync(api);
    const oneEndpoint = endpoints.length === 1;

    return (
      <div data-testid="endpointSection" className="operationInformation operationEndpoints divided__item">
        <div role="heading" aria-level={3}  className="labelText">{t(`${oneEndpoint ? 'endpoint' : 'endpoints'}`)}</div>
        {(oneEndpoint || !isSoap) && (
          <div className="valueText">
            {endpoints.map((endpoint, index) => {
              return (
                <div key={endpoint.url + '_' + index} className="apiEndpointContainer">
                  <div className="apiEndpoint">
                    {endpoint.types && <div className="apiEndpointType">{endpoint.types + ':'}</div>}
                    <div className={'apiEndpointUrl'} data-testid="endpointUrl">
                      {endpoint.url}
                    </div>
                  </div>
                  {isAsync && renderServerBindings(endpoint)}
                </div>
              );
            })}
          </div>
        )}
        {!oneEndpoint && isSoap && (
          <div className="valueText">
            <Select
              id="overview_endpoint_selector"
              onChange={handleEndpointChange}
              defaultValue={endpoints[0].url}
              hideLabel
              data-testid={'overview-endpoint-selector'}
            >
              {endpoints.map((param) => {
                const paramTypesText = param.types ? param.types + ': ' : '';
                return (
                  <SelectItem
                    key={param.url}
                    value={param.url}
                    text={paramTypesText + param.url}
                    onChange={handleEndpointChange}
                    data-testid={'overview-endpoint-option'}
                  />
                );
              })}
            </Select>
          </div>
        )}
      </div>
    );
  };

  const renderExtensions = (infoSectionExtensions) => {
    return <ExtensionsDisplay label={t('info_extensions')} className="infoSectionExtensions divided__item" extensions={infoSectionExtensions} />;
  };

  const renderSecurity = (security, securityDefinitions) => {
    const mtlsEnabled = api['x-ibm-configuration'] && api['x-ibm-configuration']['application-authentication'];

    return <SecurityDisplay security={security} securityDefinitions={securityDefinitions} mtls={mtlsEnabled} className="divided__item" />;
  };

  const renderApiExtensions = () => {
    const apiLevelExtensions = apiHandler.getCustomExtensions(api);

    return <ExtensionsDisplay label={t('api_extensions')} className="apiExtensions divided__item" extensions={apiLevelExtensions} />;
  };

  const renderApiInfo = () => {
    return (
      <div className="operationInformation operationContact divided__item" data-testid={'overview-contact'}>
        <div role="heading" aria-level={3}  className="labelText">{t('api_contact_info')}</div>
        <div className="valueText">
          <div className="apiContactDetails">
            {api.info.contact.name && <div className="apiContactName">{api.info.contact.name}</div>}
            {api.info.contact.email && (
              <div className="apiContactEmail">
                <Link href={`mailto:${api.info.contact.email}`}>{api.info.contact.email}</Link>
              </div>
            )}
            {api.info.contact.url && (
              <div className="contactUrl">
                <Link href={api.info.contact.url} target="_blank" rel="noopener noreferrer">
                  {api.info.contact.url}
                  <Launch className="externalLink" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderExternalDocs = () => {
    const markupType = apiHandler.getMarkupType();

    return (
      <div className="operationInformation operationExternalLinks divided__item" data-testid={'overview-external-links'}>
        <div role="heading" aria-level={3}  className="labelText">{t('explorer_external_links')}</div>
        <div className="valueText">
          {api.externalDocs.description && (
            <Markdown className={'externalLinkDescription'} data={getTranslatedExtensions(api.externalDocs, 'description')} markupType={markupType} />
          )}
          <Link href={api.externalDocs.url} target="_blank" rel="noopener noreferrer" data-testid="externalDocsUrl">
            {api.externalDocs.url}
            <Launch className="externalLink" />
          </Link>
        </div>
      </div>
    );
  };

  const renderAdditionalDocumentation = () => {
    return (
      <div className="operationInformation operationAdditionalDocumentation divided__item" data-testid={'overview-additional-docs'}>
        <div role="heading" aria-level={3}  className="labelText">{t('explorer_additional_docs')}</div>
        <div className="valueText">
          <div className="additionalDocumentationLinks">
            {documentationEntries.map((documentationEntry, index) => {
              return (
                <Link
                  key={'documentation_' + index}
                  tabIndex="0"
                  role="link"
                  className="additionalDocumentationLink clickable truncate"
                  title={documentationEntry.title}
                  onKeyDown={handleKeyDown(() => showModal(null, documentationEntry))}
                  onClick={() => showModal(null, documentationEntry)}
                >
                  {documentationEntry.title}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderDownload = () => {
    const downloadTypeYaml = api.asyncapi ? t('download_asyncapi_yaml') : t('download_oai_yaml');
    const downloadTypeJson = api.asyncapi ? t('download_asyncapi_json') : t('download_oai_json');
    return (
      <div className="block-link divided__item" data-testid={'overview-download-api'}>
        <div className="downloadOpenApi">
          {!isIE() && (
            //TODO replace with ButtonMenu from ibm-products
            <>
              <Link className="apiDownload" title={downloadTypeJson} download={jsonFileContentState.filename} href={jsonFileContentState.fileContent}>
                {downloadTypeJson}
                <Download aria-label={t('download_icon')} className="apiDownload clickable" />
              </Link>
              <br />
              <Link className="apiDownload" title={downloadTypeYaml} download={yamlFileContentState.filename} href={yamlFileContentState.fileContent}>
                {downloadTypeYaml}
                <Download aria-label={t('download_icon')} className="apiDownload clickable" />
              </Link>
            </>
          )}
          {isIE() && (
            //TODO replace with ButtonMenu from ibm-products
            <>
              <Link
                key={'apiDownload'}
                className="apiDownload clickable"
                title={downloadTypeJson}
                onClick={(evt) => downloadApiForIE(evt, 'json')}
                onKeyDown={(evt) => downloadApiForIE(evt, 'json')}
                role="link"
                tabIndex="0"
              >
                {downloadTypeJson}
                <Download aria-label={t('download_icon')} className="apiDownload clickable" />
              </Link>
              <br />
              <Link
                key={'apiDownload'}
                className="apiDownload clickable"
                title={downloadTypeYaml}
                onClick={downloadApiForIE}
                onKeyDown={downloadApiForIE}
                role="link"
                tabIndex="0"
              >
                {downloadTypeYaml}
                <Download aria-label={t('download_icon')} className="apiDownload clickable" />
              </Link>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderTermsOfService = () => {
    return (
      <div className={classNames('block-link"')}>
        <div className="termsOfServiceUrl" data-testid={'overview-tos'}>
          {api.info.termsOfService.startsWith('http') ? (
            <Link href={api.info.termsOfService} target="_blank" rel="noopener noreferrer">
              {t('terms_of_service')}
              <Launch className="externalLink" />
            </Link>
          ) : (
            <Link
              tabIndex="0"
              role="link"
              className="termsOfServiceLink clickable truncate"
              title={t('terms_of_service')}
              onKeyDown={handleKeyDown(() => showModal(null, { title: t('terms_of_service'), content: api.info.termsOfService, markdown: true }))}
              onClick={() => showModal(null, { title: t('terms_of_service'), content: api.info.termsOfService, markdown: true })}
            >
              {t('terms_of_service_link')}
            </Link>
          )}
        </div>
      </div>
    );
  };

  const renderInfoSectionEntries = () => {
    return infoSectionEntriesState.map(function (infoSectionEntry, index) {
      if ((!infoSectionEntry.location || infoSectionEntry.location === 'OVERVIEW') && infoSectionEntry.html) {
        return (
          <div
            key={'infoSectionEntry_' + index}
            className={classNames('infoSection', infoSectionEntry.class)}
            dangerouslySetInnerHTML={{ __html: infoSectionEntry.html }}
            data-testid={'overview-infosection'}
          />
        );
      }
      return null;
    });
  };

  const renderProtocol = () => {
    let protocolDisplay;
    if (api.asyncapi) {
      let protocol = apiHandler.getFirstProtocol();
      protocolDisplay = displayType(protocol);
      if (isKafka(protocol)) {
        protocolDisplay = isKafkaSecure(protocol) ? t('encrypted_protocol', { protocol: protocolDisplay }) : protocolDisplay;
        const docLink = get(options, 'apiTypeDocs.kafka');
        if (docLink) {
          protocolDisplay = (
            <Link href={docLink} target="_blank" rel="noopener noreferrer" title={t('kafka_doc_link_title')}>
              {displayType(protocolDisplay)}
              <Launch className="externalLink" />
            </Link>
          );
        }
      }
    } else {
      protocolDisplay = apiHandler.getSchemes(api).join(', ').toUpperCase();
    }

    return (
      protocolDisplay && (
        <div className="operationInformation apiProtocol divided__item">
          <div role="heading" aria-level={3}  className="labelText">{t('protocol')}</div>
          <div className="valueText" data-testid="api-protocol">
            {protocolDisplay}
          </div>
        </div>
      )
    );
  };

  const renderBody = () => {
    let endpoints = apiHandler.getEndpoints(api, {});
    let isSoap = apiHandler.isSOAP(api);
    let infoSectionExtensions = apiHandler.getCustomExtensions(api.info);
    let summary = api.info && (api.info.summary || api.info['x-ibm-summary']);
    let description = get(api, 'info.description');
    let apiLevelExtensions = apiHandler.getCustomExtensions(api);
    let security = apiHandler.getSecurity();
    let securityDefinitions = apiHandler.getSecurityDefinitions();
    let markupType = apiHandler.getMarkupType();

    const hasSecurity = security && security.length > 0 && securityDefinitions && Object.keys(securityDefinitions).length > 0;

    return (
      <section className="overviewBody divided" aria-label="overview-body">
        {summary
          ? renderSummary(summary)
          : description && (
              <Markdown
                className="apiDescription"
                data={getTranslatedExtensions(api.info, 'description')}
                markupType={markupType}
                data-testid={'overview-x-ibm-summary'}
              />
            )}
        {summary && description && renderDescription(markupType)}
        {renderType()}
        {renderProtocol()}
        {Array.isArray(endpoints) && endpoints.length > 0 && renderEndpoints(endpoints)}
        {infoSectionExtensions && renderExtensions(infoSectionExtensions)}
        {hasSecurity && renderSecurity(security, securityDefinitions)}
        {apiLevelExtensions && renderApiExtensions()}
        {api.info && api.info.contact && (api.info.contact.name || api.info.contact.email || api.info.contact.url) && renderApiInfo()}
        {api.externalDocs && renderExternalDocs()}
        {documentationEntries && documentationEntries.length > 0 && renderAdditionalDocumentation()}
        {(!isSoap || (isSoap && options.showSoapSwaggerDownload)) &&
          (!api.asyncapi || (api.asyncapi && options.showAsyncApiDownload)) &&
          options.showSwaggerDownload &&
          renderDownload()}
        {api.info && api.info.termsOfService && renderTermsOfService()}
        {infoSectionEntriesState && infoSectionEntriesState.length > 0 && renderInfoSectionEntries()}
      </section>
    );
  };

  const renderModal = () => {
    let markupType = apiHandler.getMarkupType();

    return (
      <Modal
        open={modalState.modalOpen}
        passiveModal={true}
        modalHeading={modalState.modalTitle}
        onRequestClose={hideDocumentation}
        modalLabel={null}
        data-testid={'overview-modal'}
        hasScrollingContent={true}
        aria-label={t('model_label')}
      >
        {!modalState.modalContent && <Loading small withOverlay={false} />}
        {modalState.modalContent && !modalState.modalMarkdown && <div dangerouslySetInnerHTML={{ __html: modalState.modalContent }} />}
        {modalState.modalContent && modalState.modalMarkdown && <Markdown data={modalState.modalContent} markupType={markupType} />}
      </Modal>
    );
  };

  const logSwaggerMessages = () => {
    swaggerState.showSwaggerMessages &&
      swaggerState.swaggerValidationSkipped &&
      !swaggerState.swaggerValidationOverride &&
      !swaggerState.swaggerErrors &&
      console.log(t('swagger_validation_size_warning_description'));
  };

  let apiType = apiHandler.getType(api);

  logSwaggerMessages();

  return (
    <div
      className={classNames('overview-section', 'main', `${apiType.toLowerCase()}-api`, `overview-${cleanUpKey(api.info.title + '_' + api.info.version)}`)}
      aria-label="overview-main"
      role="region"
    >
      {swaggerState.showSwaggerMessages && swaggerState.swaggerErrors && renderSwaggerErrors(swaggerState.swaggerErrors)}
      {apiType !== 'GraphQL' && renderHeader()}
      {renderBody()}
      {renderModal()}
    </div>
  );
};

Overview.propTypes = {
  documentationEntries: PropTypes.array, // An array of additional documentation elements,
  infoSectionEntries: PropTypes.array //Elements to be added to the info section
};

export default Overview;
